/**
 * Repairs an API-shaped assessment document so the Zod schema can parse it (B7 / api-contract.md).
 *
 * `AssessmentTemplate::casts()` declares `'data' => 'array'`, so the document passes through a PHP
 * array on the write path, where the map/list distinction does not exist. Two consequences reach us:
 *
 *   1. any empty JSON object at any depth comes back as an empty ARRAY (`{"rubrics":{}}` →
 *      `{"rubrics":[]}`), which fires on the ordinary FIRST SAVE of a template whose rubric catalog
 *      is still empty — not an edge case;
 *   2. a non-empty map keyed by the sequential numeric strings `"0"`, `"1"`, … is indistinguishable
 *      from a list, so it also returns as an array with the keys lost.
 *
 * `RubricCatalog` is `Record<string, ReviewRubric>`, so `z.record(...)` rejects the array and a
 * document the user never edited fails to load. The backend cannot fix this: undoing it needs to
 * know which `data` keys are maps, which is document schema knowledge decision 3 forbids it from
 * holding. Both cases have the same inverse — index → `String(index)` is exactly the key PHP
 * dropped, and an empty array maps to an empty map.
 *
 * It lives beside `schema.ts`, not in `src/utils/helpers/`, because the list of map-typed fields IS
 * schema knowledge and must change in the same edit; a copy in `utils/` would drift silently.
 *
 * A SECOND repair with the same shape — the legacy `{ code, label }` standard — shares this caller,
 * since both turn what the store holds into what the schema describes and both must run before the
 * document is parsed.
 *
 * Called by the API hooks at the single point where a response becomes the working document (show
 * AND every save response, or the second save re-submits an array where a map belongs), and
 * defensively by `useValidateDefinition`.
 */

/**
 * Every map-typed (`z.record`) field in the document, as a path of object keys from the root.
 * ADDING THE NEXT MAP-TYPED FIELD IS ONE LINE HERE — no new branch. Only `data.rubrics` qualifies
 * today. The paths are plain object keys: a map nested inside an array would need a wildcard
 * segment, which does not exist because nothing in the schema needs it.
 */
export const DOCUMENT_MAP_PATHS: readonly (readonly string[])[] = [
  ['rubrics'],
]

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** `[a, b]` → `{ '0': a, '1': b }`; `[]` → `{}`. The inverse of PHP's map→list flattening. */
const arrayToMap = (list: readonly unknown[]): Record<string, unknown> =>
  Object.fromEntries(list.map((entry, index) => [String(index), entry]))

/** Rebuilds `node` with the value at `path` coerced from array to map. Non-mutating: returns
 *  the original reference untouched when there is nothing to repair. */
const repairAt = (node: unknown, path: readonly string[]): unknown => {
  if (path.length === 0) {
    return Array.isArray(node) ? arrayToMap(node) : node
  }
  if (!isRecord(node)) {
    return node
  }
  const head = path[0]
  if (head === undefined || !(head in node)) {
    return node
  }
  const repaired = repairAt(node[head], path.slice(1))
  if (repaired === node[head]) {
    return node
  }
  return { ...node, [head]: repaired }
}

// --- Repair 2 — the legacy `{ code, label }` standard ----------------------
//
// `Activity.standards` and `ReviewRubric.standards` are `string[]` (bare codes). They were
// `Standard[]` until lib-core-types `7af75cd`, and any document authored before that bump still
// holds objects: the schema accepts only strings, so such a document could never be saved again from
// the editor, and the picker would render `[object Object]` chips and throw in its comparator.
//
// The conversion is lossless in the direction that matters: the `label` was a copy of the catalogue
// statement (B12.1.3 dropped it deliberately), and the picker resolves the statement by code.
//
// It cannot be a `DOCUMENT_MAP_PATHS` entry: `standards` sits inside arrays and inside the rubric
// map's values, which the plain object-key paths above cannot address.

/** `{ code, label }` → `code`. Anything else — a bare code already, or a shape neither the old
 *  nor the new contract describes — passes through untouched, for the schema to reject. */
const toStandardCode = (entry: unknown): unknown =>
  isRecord(entry) && typeof entry.code === 'string' ? entry.code : entry

/** Rebuilds `node[key]` by mapping the entries of the array it holds. Non-mutating, and returns
 *  the original reference when every entry mapped to itself. */
const mapArrayAt = (node: unknown, key: string, map: (entry: unknown) => unknown): unknown => {
  if (!isRecord(node) || !Array.isArray(node[key])) {
    return node
  }
  const source: readonly unknown[] = node[key]
  const mapped = source.map(map)
  return mapped.every((entry, index) => entry === source[index]) ? node : { ...node, [key]: mapped }
}

/** As `mapArrayAt`, for the values of a map-typed field. */
const mapValuesAt = (node: unknown, key: string, map: (value: unknown) => unknown): unknown => {
  if (!isRecord(node) || !isRecord(node[key])) {
    return node
  }
  const source: Record<string, unknown> = node[key]
  const entries = Object.entries(source).map(([entryKey, value]) => [entryKey, map(value)] as const)
  return entries.every(([entryKey, value]) => value === source[entryKey])
    ? node
    : { ...node, [key]: Object.fromEntries(entries) }
}

/** As `mapArrayAt`, for a single child node. */
const mapChildAt = (node: unknown, key: string, map: (child: unknown) => unknown): unknown => {
  if (!isRecord(node) || !(key in node)) {
    return node
  }
  const mapped = map(node[key])
  return mapped === node[key] ? node : { ...node, [key]: mapped }
}

/** Both owners of a `standards` array — an activity and a rubric — repair identically. */
const repairStandardsOwner = (owner: unknown): unknown => mapArrayAt(owner, 'standards', toStandardCode)

/**
 * Converts every legacy standard in the document to a bare code, at both places the schema puts one:
 * `definition.screens[i].activities[j].standards` and `rubrics[id].standards`. Runs AFTER the
 * map/list repair, so the rubric catalog is already a map. Non-mutating and idempotent.
 */
const repairLegacyStandards = (document: unknown): unknown => {
  const withActivities = mapChildAt(document, 'definition', definition =>
    mapArrayAt(definition, 'screens', screen =>
      mapArrayAt(screen, 'activities', repairStandardsOwner)),
  )
  return mapValuesAt(withActivities, 'rubrics', repairStandardsOwner)
}

/**
 * Normalises an API-shaped document into the shape the Zod schema expects.
 *
 * The input is `unknown` on purpose: `AssessmentTemplateDocument` describes the NORMALISED shape, so
 * anything strong enough to name the input would be a lie about what the API sends. The output stays
 * `unknown` too — this repairs, it does not validate. Non-mutating and idempotent.
 */
export const normalizeAssessmentDocument = (raw: unknown): unknown =>
  repairLegacyStandards(
    DOCUMENT_MAP_PATHS.reduce<unknown>((document, path) => repairAt(document, path), raw),
  )
